/**
 * Flight API Type Definitions
 *
 * Types based on the Mavlink API schema at https://mavlink-be.democoncept.id/v3/api-docs
 */

import { z } from 'zod';

/**
 * Flight status enum
 */
export type FlightStatus = 'PENDING' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'ABORTED';

/**
 * AOI info in flight response
 */
export interface AoiInfo {
    aoiId: string;
    aoiName: string;
}

/**
 * Flight response from API
 */
export interface FlightResponse {
    flightId: string;
    flightName: string;
    description?: string;
    status: FlightStatus;
    missionId?: string;
    missionName?: string;
    areasOfInterest?: AoiInfo[];
    aiEnabled?: boolean;
    videoStreamUrl?: string;
    startTime?: string;
    endTime?: string;
    totalDistance?: number;
    totalDuration?: number;
    telemetryCount?: number;
    createdAt: string;
    updatedAt: string;
}

/**
 * Flight create request
 */
export interface FlightCreateRequest {
    flightName: string;
    description?: string;
    pilotId: string;
    missionId?: string;
    aoiIds?: string[];
    aiEnabled?: boolean;
}

/**
 * Telemetry response
 */
export interface TelemetryResponse {
    id: number;
    timestamp: string;
    latitude: number;
    longitude: number;
    altitude: number;
    groundSpeed: number;
    heading: number;
    roll: number;
    pitch: number;
    yaw: number;
    batteryRemaining: number;
    batteryVoltage: number;
}

/**
 * Battery data response
 */
export interface BatteryDataResponse {
    timestamp: string;
    batteryRemaining: number;
    batteryVoltage: number;
}

/**
 * GeoJSON geometry
 */
export interface GeoJsonGeometry {
    type: string;
    coordinates: number[][];
}

/**
 * GeoJSON response for flight path
 */
export interface GeoJsonResponse {
    type: string;
    geometry: GeoJsonGeometry;
    properties: Record<string, unknown>;
}

/**
 * Pagination info
 */
export interface PageableObject {
    pageNumber: number;
    pageSize: number;
    offset: number;
    paged: boolean;
    unpaged: boolean;
    sort: SortObject;
}

export interface SortObject {
    sorted: boolean;
    unsorted: boolean;
    empty: boolean;
}

/**
 * Paginated flight response
 */
export interface PageFlightResponse {
    content: FlightResponse[];
    totalElements: number;
    totalPages: number;
    numberOfElements: number;
    size: number;
    number: number;
    first: boolean;
    last: boolean;
    empty: boolean;
    pageable: PageableObject;
    sort: SortObject;
}

/**
 * Flight list filters
 */
export interface FlightListFilters {
    page?: number;
    size?: number;
    status?: FlightStatus;
    missionId?: string;
}

/**
 * Telemetry query params
 */
export interface TelemetryQueryParams {
    from?: string;
    to?: string;
}

// =============================================================================
// Form Types (New Flight Page)
// =============================================================================

/**
 * Flight Setup Form Schema
 */
export const flightSetupSchema = z.object({
    flightName: z.string().min(1, 'Flight Name is required'),
    selectMission: z.string().min(1, 'Please select a mission'),
    selectAOIs: z.array(z.string()).min(1, 'At least one Area of Interest is required'),
    selectPilot: z.string().min(1, 'Please select a pilot'),
    aiEnabled: z.boolean().optional(),
    flightDescription: z.string().optional(),
});

export type FlightSetupFormData = z.infer<typeof flightSetupSchema>;

/**
 * Drone Option for select input
 */
export interface DroneOption {
    value: string;
    label: string;
}

/**
 * AOI Option for select input
 */
export interface AOIOption {
    value: string;
    label: string;
}

/**
 * User Option for select input
 */
export interface UserOption {
    value: string;
    label: string;
}
